import { useMemo, useState } from 'react';
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { openDocumentPreview } from './DocumentPreviewSlice_Store';
import { setLayoutMode } from './LayoutSlice_Store';
import { canPreviewDocument } from './services/documentPreviewService';
import { useAppDispatch, useAppSelector } from './hooks/reduxHooks';
import NonPreviewableDocumentModal from './components/NonPreviewableDocumentModal';

// Document type interface
interface DocumentData {
  docId: string;
  dealId?: string;
  dealName?: string;
  documentName?: string;
  originalFilename?: string;
  summaryText?: string;
  documentCategory?: string;
  mimeType?: string;
  fileSize?: number;
  processingStatus?: string;
  createdAt?: string;
  updatedAt?: string;
  documentOrigin?: string;
  filePath?: string;
  sharepointSiteId?: string;
  sharepointDriveId?: string;
  sharepointItemId?: string;
}

// Deal type interface
interface DealData {
  dealId: string;
  dealName?: string;
}

/**
 * Extract docId from document
 */
const getDocumentId = (document: Partial<DocumentData> | null | undefined): string | null => {
  if (!document) {
    return null;
  }

  const docId = document.docId;

  if (!docId) {
    return null;
  }

  return docId;
};

const getDocumentDisplayName = (document: Partial<DocumentData> | null | undefined): string =>
  document?.originalFilename ||
  document?.documentName ||
  "Untitled Document";

const DocsTable_Component = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const [nonPreviewableDoc, setNonPreviewableDoc] = useState<DocumentData | null>(null);

  const documents = useAppSelector((state) => state.resultsSlice.documents || []) as DocumentData[];
  const deals = useAppSelector((state) => state.resultsSlice.deals || []) as DealData[];

  // Create a map of deal IDs to deal names for quick lookup
  const dealMap = useMemo(() => {
    const map: Record<string, string> = {};
    deals.forEach(deal => {
      const dealId = deal.dealId;
      if (dealId) {
        map[dealId] = deal.dealName || "Unknown Deal";
      }
    });
    return map;
  }, [deals]);

  // Flatten documents from the results payload (including unlinked docs)
  const flattenedDocuments = useMemo(() => {
    if (!documents || documents.length === 0) {
      return [];
    }

    return documents.map((doc) => {
      const dealId = doc.dealId;
      const dealName = doc.dealName || (dealId ? dealMap[dealId] : undefined);
      const originalFilename = doc.originalFilename || doc.documentName;

      const enrichedDocument: DocumentData = {
        ...doc,
        docId: doc.docId,
        dealId,
        dealName: dealName || (dealId ? dealMap[dealId] : "Unlinked Document"),
        originalFilename: originalFilename
      };

      return enrichedDocument;
    });
  }, [documents, dealMap]);

  const handleDocumentClick = (document: DocumentData): void => {
    // Build document object for preview with all necessary fields (Phase 4: camelCase only)
    const documentForPreview = {
      originalFilename: document.originalFilename,
      docId: document.docId,
      filename: document.documentName,
      summaryText: document.summaryText,
      documentCategory: document.documentCategory,
      mimeType: document.mimeType,
      fileSize: document.fileSize,
      processingStatus: document.processingStatus,
      createdAt: document.createdAt,
      updatedAt: document.updatedAt,
      documentOrigin: document.documentOrigin,
      filePath: document.filePath,
      sharepointSiteId: document.sharepointSiteId,
      sharepointDriveId: document.sharepointDriveId,
      sharepointItemId: document.sharepointItemId
    };

    // Check if document can be previewed (based on file extension)
    if (canPreviewDocument(documentForPreview)) {
      // Open document preview
      dispatch(openDocumentPreview(documentForPreview));
      // Set layout mode to workspace when document is opened
      dispatch(setLayoutMode('workspace'));
    } else {
      setNonPreviewableDoc(document);
    }
  };

  if (flattenedDocuments.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {deals.length === 0
          ? "Run a deal search to see related documents here."
          : "No documents were returned for these deals."
        }
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-1 w-full min-w-0 border border-cvc-tableBorder rounded-[5px] overflow-y-auto overflow-x-auto">
      <NonPreviewableDocumentModal
        isOpen={nonPreviewableDoc !== null}
        onClose={() => setNonPreviewableDoc(null)}
        documentName={getDocumentDisplayName(nonPreviewableDoc)}
        filePath={nonPreviewableDoc?.filePath}
        sharepointSiteId={nonPreviewableDoc?.sharepointSiteId}
        sharepointDriveId={nonPreviewableDoc?.sharepointDriveId}
        sharepointItemId={nonPreviewableDoc?.sharepointItemId}
      />
      <table className="min-w-full tablet:min-w-[600px] bg-white">
        <thead className="bg-cvc-tableHeader sticky top-0 z-10">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-cvcBody font-normal text-cvc-headerText">
              Deal Name
            </th>
            <th className="px-4 py-3 text-left text-sm font-cvcBody font-normal text-cvc-headerText">
              File Name
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-cvc-tableBorder font-cvcBody">
          {flattenedDocuments.map((document) => {
            const docId = getDocumentId(document);
            const documentName = getDocumentDisplayName(document);

            // Skip documents without docId
            if (!docId) {
              return null;
            }

            return (
              <tr
                key={docId}
                className="hover:bg-cvc-lb2 cursor-pointer transition-colors duration-200"
                onClick={() => handleDocumentClick(document)}
              >
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <span className="text-cvc-blue bg-cvc-linkBg rounded-[3px] px-1">{document.dealName || 'Unknown Deal'}</span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-cvc-text">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-4 w-4 text-cvc-headerText mr-2" />
                    {documentName}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default DocsTable_Component;
